import { type ICharacter, useApp, useUserInfo } from "@next-platform/client-common-core";
import { useCallback, useMemo, useState } from "react";

import { useRecombee } from "@/hooks";
import { CharacterRatingWeights } from "@/pages/Chat/components/constants";
import useDiscoverLessLikeThisToast from "@/pages/Discover/hooks/useDiscoverLessLikeThisToast";
import { removeRecommendationFromLocalStorage } from "@/utils";

const readCharacterId = (character: ICharacter): string => character.character_id || character.id;

// Discover resolves its NSFW browse selection through the capability rules while
// Home reads the stored toggle, so the two can land on different cache entries.
// Both are purged: clearing only one leaves the dismissed chatbot in the entry
// the other surface reads, and it returns there for the rest of the cache window.
const RECOMMENDATION_CACHE_NSFW_TOGGLES = [false, true] as const;

export type DiscoverLessLikeThis = {
  /** Recommended chatbots the viewer asked to see less of; the page drops them from the row, as Home does. */
  readonly dismissedCharacterIds: readonly string[];
  readonly lessLikeThis: (character: ICharacter) => void;
};

/**
 * The single "less like this" path for Discovery: the card menu and the profile overlay both rate the
 * chatbot, drop it from the row and confirm with the same toast. The page owns the dismissed ids so a
 * chatbot dismissed from the overlay is gone from the row once the overlay closes.
 */
export default function useDiscoverLessLikeThis(): DiscoverLessLikeThis {
  const { onlyUserId } = useUserInfo();
  const { recombeeRecommendationsConfig } = useApp();
  const { sendUserInteractionRating } = useRecombee();
  const notifyLessLikeThis = useDiscoverLessLikeThisToast();
  const [stateDismissedCharacterIds, setStateDismissedCharacterIds] = useState<readonly string[]>([]);

  const lessLikeThis = useCallback(
    (character: ICharacter) => {
      // Both entry points are hidden from guests; this only guards a malformed session.
      if (!onlyUserId) {
        return;
      }

      const characterId = readCharacterId(character);
      void sendUserInteractionRating(onlyUserId, characterId, CharacterRatingWeights.DISLIKE);
      notifyLessLikeThis();

      // Only Recombee picks leave the row; curated results stay, as on Home.
      if (!character.recommId) {
        return;
      }

      setStateDismissedCharacterIds((current) => (current.includes(characterId) ? current : [...current, characterId]));
      RECOMMENDATION_CACHE_NSFW_TOGGLES.forEach((nsfwToggle) => {
        removeRecommendationFromLocalStorage({
          characterId,
          nsfwToggle,
          homepageRecommendationsFetchCount: recombeeRecommendationsConfig?.homepageRecommendationsFetchCount,
          homepageRecommendationsDislikedAllowedToRemoveCount: recombeeRecommendationsConfig?.homepageRecommendationsDislikedAllowedToRemoveCount,
          recommendationspageFetchCount: recombeeRecommendationsConfig?.recommendationspageFetchCount,
          recommendationspageDislikedAllowedToRemoveCount: recombeeRecommendationsConfig?.recommendationspageDislikedAllowedToRemoveCount,
        });
      });
    },
    [notifyLessLikeThis, onlyUserId, recombeeRecommendationsConfig, sendUserInteractionRating],
  );

  return useMemo(() => ({ dismissedCharacterIds: stateDismissedCharacterIds, lessLikeThis }), [lessLikeThis, stateDismissedCharacterIds]);
}
